// Ported from vlookup-dramatic/app/page.tsx by scripts/revamp/port-design.js
import React, { lazy, Suspense, useEffect, useState } from "react";
import Image from "../../components/image";
import Link from "../../components/link";
import { ArrowUpRight, ArrowRight, Check, MessageCircle, Mail, } from "../../lib/icons";
import { AppStoreBadge, GooglePlayBadge, AppQrCodes, } from "../../components/store-badges";
import { AppPhone } from "../../components/app-phone";
import { ButtonLink } from "../../components/button-link";
import { Icon } from "../../components/icon";
import { LinkedInLogo } from "../../components/linkedin-logo";
import { cn } from "../../lib/utils";
import { featuredMentors, partners, howVLookUpWorks, communitySection, appSectionHeading, appSectionSub, appSectionPoints, partnersHeading, testimonialsHeading, } from "../../lib/content";
import { HomeHero } from "./home-hero";
import { HomeAudience } from "./home-audience";
import { HomeReveal } from "./home-reveal";
import { useSelector, useDispatch } from "react-redux";
import * as settingActions from "../../../actions/settingActions";
import * as homePageActions from "../../../actions/homePageActions";
import { agent } from "../../../utils/agent";
import * as API from "../../../utils/apiPath";
import { showToasterWithOk } from "../../../utils/helpers";
import { usePageMeta } from "../../hooks/use-page-meta";
import { useLoggedIn } from "../../hooks/use-logged-in";

const HomeTestimonialsCarousel = lazy(() => import("./home-testimonials"));
const InstagramSection = lazy(() => import("./instagram-section"));

/* ── Live data (revamp plan, "Existing API Wiring" sheet) ─────────────────────
   The design was built on fixed sample data (lib/content.js). The calls the old
   home page already made are re-attached here; the sample data is only the
   fallback while a call is loading or returns nothing. */
const isAvailable = (m) => !m.is_reached_limit &&
    Number(m.user_availability_check?.availability ?? m.user_availability ?? 1) === 1;
const mentorFromApi = (m) => ({
    name: m.full_name, role: m.position, company: m.organization, domain: m.main_area,
    available: isAvailable(m), img: m.image, linkedin: m.linkedin,
});
/* QA row 37: the testimonials API has no role flag, so tag the known mentee
   authors by name (client-confirmed list). Everyone else stays "Mentor" —
   ask the client/admin to add a role field for a proper fix. */
const MENTEE_AUTHORS = ["raani", "sindhura", "khushi", "nina"];
const testimonialFromApi = (t) => ({
    quote: t.description, name: t.full_name,
    role: [t.position, t.organization].filter(Boolean).join(" · "),
    chip: MENTEE_AUTHORS.some((n) => (t.full_name || "").toLowerCase().includes(n))
        ? "Mentee"
        : "Mentor",
    img: t.image,
});

export default function Home() {
    const dispatch = useDispatch();
    const homePageData = useSelector((s) => s.homePageData) || {};
    useEffect(() => {
        dispatch(settingActions.getBrandData());        // partner logos
        dispatch(homePageActions.loadHomePageData("home")); // page title/description (CMS)
        dispatch(homePageActions.loadLeaderData(10));   // diversity champions
        dispatch(homePageActions.loadTestimonialsData(10));
    }, [dispatch]);
    usePageMeta({
        title: homePageData.meta_title || "vLookUp | Creating impactful mentoring connections for women",
        description: homePageData.meta_description,
    });
    const loggedIn = useLoggedIn();
    return (<div className="overflow-x-clip">
      <HomeHero />
      <HomeAudience />
      <HowVLookUpWorks />
      <FeaturedMentors />
      <Partners />
      <Testimonials />
      <Suspense fallback={null}>
        <InstagramSection />
      </Suspense>
      <AppSection />
      {!loggedIn && <Newsletter />}
    </div>);
}
/* ─────────────────  HOW vLOOKUP WORKS (light spotlight)  ───────────────── */
function HowVLookUpWorks() {
    return (<section id="how-it-works" className="wrap py-6 md:py-10">
      <div className="relative isolate overflow-hidden rounded-[2.5rem] bg-cream-deep px-8 py-12 ring-1 ring-brand-navy/5 md:rounded-[3rem] md:px-14 md:py-16">
        <div className="pointer-events-none absolute -right-24 -top-24 size-[380px] rounded-full bg-brand-blue/15 blur-3xl"/>
        <div className="pointer-events-none absolute -bottom-28 -left-16 size-[320px] rounded-full bg-brand-blue/15 blur-3xl"/>

        <div className="relative">
          <div className="max-w-2xl">
            <Eyebrow>{howVLookUpWorks.eyebrow}</Eyebrow>
            <h2 className="mt-5 text-4xl font-semibold leading-[1.05] text-heading md:text-5xl">
              Everything you need to start mentoring with{" "}
              <em className="font-normal italic text-brand-red">confidence</em>
            </h2>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-muted-foreground">
              {howVLookUpWorks.sub}
            </p>
          </div>

          <HomeReveal className="mt-10">
            <div className="grid gap-5 md:grid-cols-3">
              {howVLookUpWorks.items.map((item) => (<Link key={item.title} href={item.href} className="group relative flex flex-col overflow-hidden rounded-3xl bg-card p-7 shadow-sm ring-1 ring-brand-navy/5 transition-all hover:-translate-y-1 hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red">
                  <div className="relative flex items-start justify-between">
                    <Icon name={item.icon} className="size-12"/>
                    <span className="flex size-10 items-center justify-center rounded-full bg-cream-deep text-brand-navy transition-colors group-hover:bg-brand-red group-hover:text-white">
                      <ArrowUpRight className="size-5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"/>
                    </span>
                  </div>
                  <h3 className="relative mt-5 font-display text-xl font-semibold text-heading md:text-2xl">
                    {item.title}
                  </h3>
                  <p className="relative mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">
                    {item.desc}
                  </p>
                  <span className="relative mt-5 inline-flex items-center gap-2 text-sm font-bold text-brand-navy transition-colors group-hover:text-brand-red">
                    {item.cta}
                    <ArrowRight className="size-4 transition-transform group-hover:translate-x-1"/>
                  </span>
                </Link>))}
            </div>
          </HomeReveal>
        </div>
      </div>
    </section>);
}
/* ────────────────  FEATURED MENTORS — global community  ──────────────── */
function FeaturedMentors() {
    // GET leader?limit=10 — QA row 35: show the whole list like the old home
    // carousel did (it paged through all leaders, 4-up), not just the first 4.
    const leaders = useSelector((s) => s.leaders) || [];
    const mentors = leaders.length ? leaders.slice(0, 12).map(mentorFromApi) : featuredMentors;
    return (<section id="mentors" className="wrap py-14 md:py-20">
      <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div className="max-w-xl">
          <Eyebrow>{communitySection.eyebrow}</Eyebrow>
          {/* communitySection.heading, with the closing word as the italic accent */}
          <h2 className="mt-5 text-4xl font-semibold leading-[1.05] text-heading md:text-6xl">
            {communitySection.heading.replace(/ \w+$/, "")}{" "}
            <em className="font-normal italic text-brand-red">
              {communitySection.heading.split(" ").slice(-1)[0]}
            </em>
          </h2>
        </div>
        <ButtonLink href={communitySection.browseCta.href} variant="ghost" className="group h-auto w-fit rounded-full px-0 text-[15px] font-bold text-brand-red hover:bg-transparent hover:text-brand-red-dark">
          {communitySection.browseCta.label}
          <ArrowRight className="size-4 transition-transform group-hover:translate-x-1"/>
        </ButtonLink>
      </div>

      <HomeReveal className="mt-10">
        {/* QA row 35: horizontal snap strip — every leader is reachable, and at
            tablet widths three tiles fit instead of two oversized ones. */}
        <div className="-mx-1 flex snap-x snap-mandatory gap-6 overflow-x-auto px-1 pb-3">
          {mentors.map((m) => (<article key={m.name} className="group w-[78%] shrink-0 snap-start overflow-hidden rounded-3xl bg-card shadow-sm ring-1 ring-brand-navy/5 transition-all hover:-translate-y-1.5 hover:shadow-xl sm:w-[46%] md:w-[31%] xl:w-[23.2%]">
              <div className="relative aspect-[4/5] overflow-hidden">
                <Image src={m.img} alt={m.name} fill sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw" className="object-cover object-top transition-transform duration-500 group-hover:scale-105"/>
                <span className={cn("absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-bold shadow-sm", m.available
                ? "bg-card text-brand-green"
                : "bg-card text-muted-foreground")}>
                  <span className={cn("size-2 rounded-full", m.available ? "bg-brand-green" : "bg-muted-foreground/50")}/>
                  {m.available ? "Available" : "Unavailable"}
                </span>
              </div>
              <div className="p-6">
                <h3 className="font-display text-xl font-semibold text-heading">
                  {m.name}
                </h3>
                <p className="mt-1 text-sm font-medium text-foreground/80">
                  {m.role}
                </p>
                <p className="text-sm text-muted-foreground">{m.company}</p>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-brand-blue/10 px-3 py-1 text-[11px] font-bold text-brand-blue">
                    {m.domain}
                  </span>
                </div>
                {m.linkedin && (<a href={m.linkedin} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex w-fit items-center gap-2 text-sm font-bold text-[#0A66C2] transition-opacity hover:opacity-80">
                  <LinkedInLogo className="size-5"/>
                  LinkedIn
                </a>)}
              </div>
            </article>))}
        </div>
      </HomeReveal>
    </section>);
}
/* ─────────────────────  PARTNERS — logo marquee  ───────────────────── */
function Partners() {
    // GET brand — QA row 36 (31 Aug): written-out organisation names (dev-site
    // treatment) instead of logo artwork, same as the /programs marquee. Names
    // come from the brand titles; the design's list is the fallback.
    const brand = useSelector((s) => s.brand) || [];
    const orgNames = brand.map((b) => (b.title || "").trim()).filter(Boolean);
    const names = orgNames.length ? orgNames : partners;
    const row = (hidden) => (<div aria-hidden={hidden || undefined} className="flex shrink-0 items-center gap-14 pr-14">
      {names.map((p) => (<span key={p} className="whitespace-nowrap font-display text-xl font-semibold text-brand-navy/40 transition-colors hover:text-brand-navy">
          {p}
        </span>))}
    </div>);
    return (<section id="impact" className="overflow-hidden border-y border-brand-navy/10 bg-cream">
      {/* marquee keyframes: styles/revamp.src.css */}
      <div className="py-10 md:py-12">
        <p className="text-center text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
          {partnersHeading}
        </p>
        <div className="relative mt-7">
          <div className="dHomeMarqueeTrack flex w-max">
            {row(false)}
            {row(true)}
          </div>
          <div className="pointer-events-none absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-cream to-transparent"/>
          <div className="pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-cream to-transparent"/>
        </div>
      </div>
    </section>);
}
/* ────────────────────────────  TESTIMONIALS  ──────────────────────── */
function Testimonials() {
    // GET testimonials?limit=10
    const apiItems = useSelector((s) => s.testimonials) || [];
    const items = apiItems.length ? apiItems.map(testimonialFromApi) : undefined;
    return (<section className="relative isolate overflow-hidden py-14 md:py-20">
      {/* subtle printed-paper texture, full-bleed behind everything */}

      <div className="wrap">
        <div className="max-w-2xl">
          <Eyebrow>In their words</Eyebrow>
          {/* testimonialsHeading, with the closing word as the italic accent */}
          <h2 className="mt-5 text-4xl font-semibold leading-[1.05] text-heading md:text-6xl">
            {testimonialsHeading.replace(/ \w+$/, "")}{" "}
            <em className="font-normal italic text-brand-red">
              {testimonialsHeading.split(" ").slice(-1)[0]}.
            </em>
          </h2>
        </div>

        <HomeReveal className="mt-10">
          <Suspense fallback={null}>
            <HomeTestimonialsCarousel items={items}/>
          </Suspense>
        </HomeReveal>
      </div>
    </section>);
}
/* ─────────────────────────────  APP SECTION  ──────────────────────── */
function AppSection() {
    return (<section className="wrap pb-6">
      <div className="relative overflow-hidden rounded-[2.5rem] bg-cream-deep px-8 py-12 ring-1 ring-brand-navy/5 md:rounded-[3rem] md:px-16 md:py-16">
        <div className="pointer-events-none absolute -right-24 -top-24 size-[380px] rounded-full bg-brand-blue/15 blur-3xl"/>
        <div className="pointer-events-none absolute -bottom-32 -left-16 size-[340px] rounded-full bg-brand-blue/15 blur-3xl"/>

        <div className="relative home-app-split grid items-center gap-14">
          <div>
            <Eyebrow>The vLookUp app</Eyebrow>
            {/* appSectionHeading, with the closing word as the italic accent */}
            <h2 className="mt-5 text-4xl font-semibold leading-[1.05] text-heading md:text-5xl">
              {appSectionHeading.replace(/ \w+$/, "")}{" "}
              <em className="font-normal italic text-brand-red">
                {appSectionHeading.split(" ").slice(-1)[0]}
              </em>
            </h2>
            <p className="mt-5 max-w-md text-lg leading-relaxed text-muted-foreground">
              {appSectionSub}
            </p>

            <ul className="mt-7 space-y-3">
              {appSectionPoints.map((point) => (<li key={point} className="flex items-center gap-3">
                  <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-brand-blue/10 text-brand-blue">
                    <Check className="size-3.5" strokeWidth={3}/>
                  </span>
                  <span className="text-[15px] text-brand-navy/75">{point}</span>
                </li>))}
            </ul>

            <div className="home-app-stores mt-8 flex flex-wrap items-center gap-3">
              <AppStoreBadge className="rounded-2xl px-5 py-3"/>
              <GooglePlayBadge className="rounded-2xl px-5 py-3"/>
            </div>

            {/* Scanning is the desktop path onto the phone — kept, but small. */}
            <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-3">
              <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-brand-navy/40">
                Or scan
              </span>
              <AppQrCodes labelClassName="text-brand-navy/55"/>
            </div>
          </div>

          {/* Hand-built app mock (not a store screenshot) */}
          {/* min-w-0 so the 300px device can shrink inside the phone-width
            panel instead of widening the grid column */}
          <div className="home-app-phone flex min-w-0 justify-center">
            <div className="relative min-w-0">
              <div aria-hidden className="pointer-events-none absolute -inset-8 -z-10 rounded-full bg-brand-blue/15 blur-3xl"/>
              <AppPhone />

              {/* Desktop only — on tablet the chip overlaps the left-column copy */}
              <div className="home-app-chip absolute bottom-28 left-0 hidden -translate-x-[calc(100%-1.25rem)] items-center gap-2.5 rounded-2xl bg-card p-3 shadow-xl ring-1 ring-brand-navy/10">
                <span className="flex size-9 items-center justify-center rounded-xl bg-brand-red/10 text-brand-red">
                  <MessageCircle className="size-4.5"/>
                </span>
                <span className="leading-tight">
                  <span className="block text-[11px] font-bold text-brand-navy">
                    New message
                  </span>
                  <span className="block text-[10px] text-muted-foreground">
                    Your mentor replied
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);
}
/* ─────────────────────────────  NEWSLETTER  ──────────────────────── */
function Newsletter() {
    // POST saveSubscribtionMail — the same call as the old home page / Subscribe popup
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const onSubmit = (e) => {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        agent.post(API.SAVESUBSCRIBTIONEMAIL, { email })
            .then((res) => {
            setBusy(false);
            showToasterWithOk(res.data.message);
            if (res.data.data === true) setEmail("");
        })
            .catch((err) => {
            setBusy(false);
            showToasterWithOk((err && err.message) || "Something went wrong. Please try again.");
        });
    };
    return (<section id="login" className="wrap py-12 md:py-16">
      <div className="flex flex-col items-center gap-8 rounded-[2.5rem] bg-cream-deep px-8 py-12 md:flex-row md:justify-between md:px-14">
        <div className="flex items-center gap-5">
          <div className="flex size-16 shrink-0 items-center justify-center rounded-2xl bg-brand-red/10 text-brand-red">
            <Mail className="size-8"/>
          </div>
          <div>
            <h2 className="font-display text-2xl font-semibold text-heading md:text-3xl">
              Subscribe to our newsletter
            </h2>
            <p className="mt-1 text-muted-foreground">
              to stay updated with vLookUp!
            </p>
          </div>
        </div>

        <form onSubmit={onSubmit} className="flex w-full max-w-md items-center gap-2 rounded-full bg-card p-2 shadow-sm ring-1 ring-brand-navy/10">
          <input type="email" name="email" required maxLength={50} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email Address" aria-label="Email Address" className="min-w-0 flex-1 bg-transparent px-5 text-sm text-brand-navy outline-none placeholder:text-muted-foreground"/>
          <button type="submit" disabled={busy} className="group inline-flex shrink-0 items-center gap-1.5 rounded-full bg-brand-red px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-brand-red-dark disabled:opacity-60">
            {busy ? "Sending…" : "Subscribe"}
            <ArrowUpRight className="size-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"/>
          </button>
        </form>
      </div>
    </section>);
}
/* ─────────────────────────────  HELPERS  ─────────────────────────── */
function Eyebrow({ children, className, }) {
    return (<span className={cn("inline-flex items-center gap-2.5 text-xs font-bold uppercase tracking-[0.2em] text-brand-red", className)}>
      <span className="h-px w-8 bg-current opacity-50"/>
      {children}
    </span>);
}
